import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import Popover from "@material-ui/core/Popover";
import EditIcon from "@material-ui/icons/Edit";
import { useDispatch, useSelector } from "react-redux";
import { updateGlobalParameterBertArchive } from "../reducers/BertArchive/Actions";
import InformationTag from "./InformationTag";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      flexGrow: 1,
      backgroundColor: "#c6c7c9",
      margin: "0 20px 20px",
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    infoButton: {
      display: "flex",
      alignSelf: "center",
    },
    popupRow: {
      flexDirection: "column",
      "& > .MuiFormControlLabel-root": {
        margin: "10px 0",
      },
      "& .MuiInputBase-root": {
        width: "100%",
        [theme.breakpoints.down("xs")]: {
          alignSelf: "flex-start",
          display: "flex",
          width: "auto",
        },
      },
    },
    popoverRootElement: {
      marginTop: 40,
      margin: "0px auto",
    },
    popupTitle: {
      fontWeight: "bold",
      padding: "5px 0",
    },
    switchControl: { marginLeft: -8 },
    switchThumbColor: {
      "& .MuiIconButton-root": {
        color: "#fafafa",
      },
    },
    popUpContent: {
      width: 300,
      backgroundColor: "#e8e9eb",
      padding: 20,
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    closeIcon: {
      position: "absolute",
      right: 20,
      top: 32,

      "&:hover": {
        cursor: "pointer",
      },
    },
    iconButton: {
      marginRight: 10,
      "& svg": {
        borderRadius: 4,
        boxShadow: "0px 2px 4px #00000080",
        backgroundColor: "#c5da86",
        borderColor: "#c5da86",
        padding: 12,
      },
    },
    edit: {
      display: "flex",
      alignItems: "center",
      height: 30,
    },
    popUpControlLabel: {
      width: "100%",
      paddingBottom: 5,
    },
    row: {
      minHeight: 64,
      "& > .MuiFormControlLabel-root": {
        padding: "5px 10px",
      },
      "& > label": {
        marginLeft: 16,
        marginRight: -11,
      },
    },
    select: {
      minWidth: 150,
      paddingTop: 8,
      borderBottom: 0,
      backgroundColor: "#fff",
      borderRadius: 4,
      "&:focus": {
        borderRadius: 4,
        backgroundColor: "#fff",
      },
      "&.popup": {
        minWidth: 200,
      },
    },
    colorBar: {
      opacity: 0.5,
    },
    controlLabel: {
      paddingRight: 10,
    },
  }),
  {
    name: "Settings",
  }
);

const GeneralSettingsBertArchive = (props) => {
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const { t } = useTranslation(["settingsBertArchive", "common"]);

  const { height, depth, doubleShelf, shelfLoad, scaleFactor } = useSelector((state) => state.BertArchive);
  const [selectedScale, setSelectedScale] = useState(scaleFactor);
  const heightOptions = useSelector((state) => {
    return state.Configuration.bertArchiveComponentOptions.find((opt) => opt.key === "height").options;
  });
  const depthOptions = useSelector((state) => {
    return state.Configuration.bertArchiveComponentOptions.find((opt) => opt.key === "depth").options;
  });
  const shelfLoadOptions = useSelector((state) => {
    return state.Configuration.bertArchiveComponentOptions.find((opt) => opt.key === "shelfLoad").options;
  });
  const heightValue = Number(height.match(/\d+/));
  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleToggle = (event) => {
    setAnchorElement(event.target);
  };

  const [anchorElement, setAnchorElement] = useState(null);
  const open = Boolean(anchorElement);
  const settings = [
    {
      key: "height",
      title: t("common:height"),
      selected: height,
      visible: true,
      options:
        heightOptions && heightOptions.length > 0
          ? heightOptions
          : [
              {
                title: "2000 mm",
                identifier: "2000",
              },
              {
                title: "2075 mm",
                identifier: "2075",
              },
              {
                title: "2500 mm",
                identifier: "2500",
              },
              {
                title: "3000 mm",
                identifier: "3000",
              },
            ],
    },

    {
      key: "depth",
      title: t("common:depth"),
      selected: depth,
      visible: true,
      options:
        depthOptions && depthOptions.length > 0
          ? depthOptions
          : [
              {
                title: "300 mm",
                identifier: "300",
              },
              {
                title: "400 mm",
                identifier: "400",
              },
              {
                title: "500 mm",
                identifier: "500",
              },
              {
                title: "600 mm",
                identifier: "600",
              },
              {
                title: "800 mm",
                identifier: "800",
              },
            ],
    },
    {
      key: "shelfLoad",
      title: t("common:shelfLoad"),
      selected: shelfLoad,
      visible: true,
      options:
        shelfLoadOptions && shelfLoadOptions.length > 0
          ? shelfLoadOptions
          : [
              {
                title: "100 kg",
                identifier: "100",
              },
              {
                title: "250 kg",
                identifier: "250",
              },
              {
                title: "350 kg",
                identifier: "350",
              },
            ],
    },
    {
      key: "shelf",
      title: t("common:doubleShelf"),
      selected: doubleShelf,
      visible: true,
      function: (e) => {
        dispatch(updateGlobalParameterBertArchive("doubleShelf", e.target.checked));
      },
    },
  ];
  const breakLayout = useMediaQuery("(min-width:1050px)");

  return (
    <div className={classes.root}>
      {!breakLayout && (
        <div>
          <div className={classes.edit}>
            <IconButton classes={{ root: classes.iconButton }} onClick={handleToggle}>
              <EditIcon style={{ color: "#262626" }} />
            </IconButton>
            <Typography variant="body1">{t("common:settingsEdit")} </Typography>
          </div>
          <Popover
            classes={{ paper: classes.popoverRootElement }}
            onClose={handleClose}
            open={open}
            anchorEl={anchorElement}
          >
            <div className={clsx(classes.popUpContent, "popoverBorder")}>
              <Typography variant="body1" className={classes.popupTitle}>
                {t("common:settingsDialogtitle")}
              </Typography>
              <Icon className={classes.closeIcon} onClick={handleClose}>
                close
              </Icon>
              <FormGroup row className={classes.popupRow}>
                {settings.map(
                  (setting, index) =>
                    setting &&
                    setting.visible &&
                    (setting.options && setting.options.length > 0 ? (
                      <>
                        <FormControlLabel
                          key={`setting_${index}`}
                          classes={{ label: classes.popUpControlLabel }}
                          control={
                            <div style={{ display: "flex", width: "100%" }}>
                              <Select
                                classes={{ root: clsx(classes.select, "popup") }}
                                variant="filled"
                                disableUnderline={true}
                                value={setting.selected}
                                onChange={(e) => {
                                  dispatch(updateGlobalParameterBertArchive(setting.key, e.target.value));
                                }}
                              >
                                {setting.options &&
                                  setting.options.map((option, i) => (
                                    <MenuItem
                                      className={classes.MenuItemOption}
                                      key={option.title + i}
                                      value={option.identifier}
                                    >
                                      <Typography variant="body1" className={classes.toolTitle}>
                                        {option.title}
                                      </Typography>
                                    </MenuItem>
                                  ))}
                              </Select>
                              {setting.key === "height" && (heightValue === 2075 || heightValue === 2500) && (
                                <div className={classes.infoButton}>
                                  <InformationTag
                                    infoText={
                                      heightValue === 2075
                                        ? t("frameHeight2075Information")
                                        : t("frameHeight2500Information")
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          }
                          label={setting.title}
                          labelPlacement="top"
                        />
                      </>
                    ) : (
                      <FormControlLabel
                        key={`setting_${index}`}
                        control={
                          <Switch
                            classes={{
                              root: classes.switchThumbColor,
                              switchBase: doubleShelf ? "activeCheckedColor" : "",
                              checked: doubleShelf ? "activeCheckedColor" : "",
                              track: clsx(classes.colorBar, "primaryButton"),
                            }}
                            checked={doubleShelf}
                            onChange={setting.function}
                            className={classes.switch}
                          />
                        }
                        label={setting.title}
                      />
                    ))
                )}
              </FormGroup>
            </div>
          </Popover>
        </div>
      )}
      {breakLayout && (
        <div>
          <FormGroup row className={classes.row}>
            {settings.map(
              (setting, index) =>
                setting &&
                setting.visible &&
                (setting.options && setting.options.length > 0 ? (
                  <>
                    <FormControlLabel
                      key={`setting_${index}`}
                      classes={{ label: classes.controlLabel }}
                      control={
                        <Select
                          classes={{ root: classes.select }}
                          variant="filled"
                          disableUnderline={true}
                          value={setting.selected}
                          onChange={(e) => {
                            dispatch(updateGlobalParameterBertArchive(setting.key, e.target.value));
                          }}
                        >
                          {setting.options &&
                            setting.options.map((option, i) => (
                              <MenuItem
                                className={classes.MenuItemOption}
                                key={option.title + i}
                                value={option.identifier}
                              >
                                <Typography variant="body1" className={classes.toolTitle}>
                                  {option.title}
                                </Typography>
                              </MenuItem>
                            ))}
                        </Select>
                      }
                      label={setting.title}
                      labelPlacement="start"
                    />
                    {setting.key === "height" && (heightValue === 2075 || heightValue === 2500) && (
                      <div className={classes.infoButton}>
                        <InformationTag
                          infoText={
                            heightValue === 2075
                              ? t("frameHeight2075Information")
                              : t("frameHeight2500Information")
                          }
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <FormControlLabel
                    key={`setting_${index}`}
                    control={
                      <Switch
                        classes={{
                          root: classes.switchThumbColor,
                          switchBase: doubleShelf ? "activeCheckedColor" : "",
                          checked: doubleShelf ? "activeCheckedColor" : "",
                          track: clsx(classes.colorBar, "primaryButton"),
                        }}
                        checked={doubleShelf}
                        onChange={setting.function}
                        className={classes.switch}
                      />
                    }
                    label={setting.title}
                  />
                ))
            )}
          </FormGroup>
        </div>
      )}
    </div>
  );
};

GeneralSettingsBertArchive.propTypes = {
  settings: PropTypes.array,
};

GeneralSettingsBertArchive.defaultProps = {
  settings: [],
};

export default GeneralSettingsBertArchive;
